import { createResource } from '@/store/utils/resource'
import teamsByQuestionnaire from './teams-by-questionnaire'
import lastQueryByUser from './lastQuery-by-user'
import { createAsyncType } from '@/store/utils/mutation-types'
import axios from 'axios'
import get from 'lodash/get'
import { createResource as createMonthFilterResource } from '@/store/utils/month-filter-resource'
import { colors } from '../../../../util/colors.js'

const monthFilterResource = createMonthFilterResource()

const types = {
  WILL_PREFETCH: 'WILL_PREFETCH',
  DID_PREFETCH: 'DID_PREFETCH',
  GET_TEAM_OVERVIEW: createAsyncType('GET_TEAM_OVERVIEW'),
  GET_TEAM_QUERY_DATES: createAsyncType('GET_TEAM_QUERY_DATES'),
  STORE_TEAM_ALERT: createAsyncType('STORE_TEAM_ALERT')
}

export default {
  namespaced: true,
  state () {
    return {
      prefetching: false,
      prefetched: false,
      directTeam: null,
      directQuestionnaire: null,
      teamOverview: null,
      teamOverviewStatus: void 0,
      teamQueryDates: null,
      teamQueryDatesStatus: void 0,
      storeTeamAlertStatus: void 0,
      ...monthFilterResource.state
    }
  },
  actions: {
    async getTeamOverview ({ commit }, { questionnaireId, teamId, months }) {
      commit(types.GET_TEAM_OVERVIEW.REQUEST, { teamId, questionnaireId })
      try {
        commit(types.GET_TEAM_OVERVIEW.SUCCESS, await axios.get(`/api/myTeam/questionnaires/${questionnaireId}/team/${teamId}`, { params: { months } }))
      } catch (e) {
        commit(types.GET_TEAM_OVERVIEW.FAILURE)
      }
    },
    async getTeamQueryDates ({ commit }, { questionnaireId, teamId}) {
      commit(types.GET_TEAM_QUERY_DATES.REQUEST, { teamId, questionnaireId })
      try {
        commit(types.GET_TEAM_QUERY_DATES.SUCCESS, await axios.get(`/api/myTeam/questionnaires/${questionnaireId}/team/${teamId}/queryDates`))
      } catch (e) {
        commit(types.GET_TEAM_QUERY_DATES.FAILURE)
      }
    },
    async storeTeamAlert ({ commit }, { team, type, questionnaire, question, lowerValue, upperValue }) {
      commit(types.STORE_TEAM_ALERT.REQUEST, { team, questionnaire, question, lowerValue, upperValue })
      try {
        commit(types.STORE_TEAM_ALERT.SUCCESS, await axios.post(`/api/team/${team}/setAlert`, {
          questionnaire,
          type,
          question,
          lowerValue,
          upperValue
        }))
      } catch (e) {
        commit(types.STORE_TEAM_ALERT.FAILURE)
      }
    },
    async prefetch (context, { refresh } = {}) {
      const { commit, dispatch, state } = context

      if (!state.prefetched || refresh) {
        commit(types.WILL_PREFETCH)

        await dispatch('questionnaires/getList', { perPage: 10 })
        const prefetch = { flag: true }

        if (state.questionnaires.items.length === 1) {
          prefetch.directQuestionnaire = state.questionnaires.items[0]

          await dispatch('teamsByQuestionnaire/getList', {
            questionnaireId: prefetch.directQuestionnaire._id,
            perPage: 10
          })

          if (state.teamsByQuestionnaire.items.length === 1) {
            prefetch.directTeam = state.teamsByQuestionnaire.items[0]
          }
        }
        commit(types.DID_PREFETCH, prefetch)
      }
    },
    ...monthFilterResource.actions
  },
  mutations: {
    [types.WILL_PREFETCH]: (state) => {
      state.prefetching = true
      state.prefetched = false
      state.directQuestionnaire = null
      state.directTeam = null
    },
    [types.DID_PREFETCH]: (state, { flag, directQuestionnaire, directTeam }) => {
      state.prefetching = false
      state.prefetched = flag
      state.directQuestionnaire = directQuestionnaire
      state.directTeam = directTeam
    },
    [types.GET_TEAM_OVERVIEW.REQUEST]: (state) => {
      state.teamOverviewStatus = 'fetching'
      state.teamOverview = []
    },
    [types.GET_TEAM_OVERVIEW.SUCCESS]: (state, result) => {
      state.teamOverviewStatus = 'success'

      // // Process team member data for the alerts
      for (const user in result.data.userAccumulations) {
        for (const question in result.data.userAccumulations[user].datasets) {
          const { labels, series, alertChart, alertActive, average, title } = result.data.userAccumulations[user].datasets[question]
          const datasets = []

          datasets.push({
            data: series,
            valueColorGradient: true
          })

          // Green 4cbd90 -> 76	189	144
          // Red ff00000 -> 255 0 0
          datasets.push({
            data: alertChart.upperValues,
            valueColorGradient: false,
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderColor: colors[1],
            backgroundColor: 'rgba(76, 189, 144, 0.08)',
            fill: 'end'
          })

          datasets.push({
            data: alertChart.lowerValues,
            valueColorGradient: false,
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderColor: '#FF0000',
            backgroundColor: 'rgba(255, 0, 0, 0.03)',
            fill: 'start'
          })

          result.data.userAccumulations[user].datasets[question] = { alertActive, labels, datasets, average, title }
        }
        console.log(result.data.userAccumulations[user])
      }

      state.teamOverview = result.data
    },
    [types.GET_TEAM_OVERVIEW.FAILURE]: state => {
      state.teamOverviewStatus = 'failure'
      state.teamOverview = []
    },
    [types.GET_TEAM_QUERY_DATES.REQUEST]: (state) => {
      state.teamQueryDatesStatus = 'fetching'
      state.teamQueryDates = []
    },
    [types.GET_TEAM_QUERY_DATES.SUCCESS]: (state, result) => {
      state.teamQueryDatesStatus = 'success'
      state.teamQueryDates = result.data
    },
    [types.GET_TEAM_QUERY_DATES.FAILURE]: state => {
      state.teamQueryDatesStatus = 'failure'
      state.teamQueryDates = []
    },
    [types.STORE_TEAM_ALERT.REQUEST]: (state) => {
      state.storeTeamAlertStatus = 'fetching'
    },
    [types.STORE_TEAM_ALERT.SUCCESS]: (state, result) => {
      state.storeTeamAlertStatus = 'success'
      if (result.data.type === 'accumulated') {
        for (const alert of state.teamOverview.team.alerts) {
          if (alert.type === 'accumulated' && String(alert.questionnaire) === String(state.teamOverview.questionnaire._id)) {
            alert.lowerValue = result.data.lowerValue
            alert.upperValue = result.data.upperValue
            return
          }
        }
        state.teamOverview.team.alerts.push(result.data)
      } else {
        for (const alert of state.teamOverview.team.alerts) {
          if (alert.type === 'question' && String(alert.questionnaire) === String(state.teamOverview.questionnaire._id) &&
            String(alert.question) === String(result.data.question)) {
              alert.lowerValue = result.data.lowerValue
              alert.upperValue = result.data.upperValue
              return
          }
        }
        state.teamOverview.team.alerts.push(result.data)
      }
    },
    [types.STORE_TEAM_ALERT.FAILURE]: state => {
      state.storeTeamAlertStatus = 'failure'
    },
    ...monthFilterResource.mutations
  },
  modules: {
    questionnaires: createResource('myTeam/questionnaires', { perPage: 10 }),
    teamsByQuestionnaire,
    lastQueryByUser
  },
  getters: {
    getQuestionnaire: state => {
      return get(state.teamOverview, 'questionnaire')
    },
    overAllAverage: state => {
      return get(state.teamOverview, 'overAllTrend.average')
    },
    overAllTrendDataset: state => {
      const overAllTrend = get(state.teamOverview, 'overAllTrend')
      if (overAllTrend) {
        const { labels, series, alertChart, alertActive } = overAllTrend
        const datasets = []

        datasets.push({
          data: series,
          valueColorGradient: true
        })

        // Green 4cbd90 -> 76	189	144
        // Red ff00000 -> 255 0 0
        datasets.push({
          data: alertChart.upperValues,
          valueColorGradient: false,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: colors[1],
          backgroundColor: 'rgba(76, 189, 144, 0.08)',
          fill: 'end'
        })

        datasets.push({
          data: alertChart.lowerValues,
          valueColorGradient: false,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.03)',
          fill: 'start'
        })

        return { alertActive, labels, datasets }
      }
      return {}
    },

    voronoiDataset: state => {
      return { data: get(state.teamOverview, 'voronoi', []) }
    },

    wordCloudData: state => {
      return get(state.teamOverview, 'wordCloudData', [])
    },

    teamAnswersDatasets: state => {
      const teamAnswers = []
      const answers = Object.values(get(state.teamOverview, 'answers', {}))
      for (const answer of answers) {
        const { labels, series, alertChart, average, averageTrend, alertActive, title, question } = answer
        const datasets = []
        datasets.push({
          data: series,
          valueColorGradient: true
        })

        // Green 4cbd90 -> 76	189	144
        // Red ff00000 -> 255 0 0
        datasets.push({
          data: alertChart.upperValues,
          valueColorGradient: false,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: colors[1],
          backgroundColor: 'rgba(76, 189, 144, 0.1)',
          fill: 'end'
        })

        datasets.push({
          data: alertChart.lowerValues,
          valueColorGradient: false,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderColor: '#FF0000',
          backgroundColor: 'rgba(255, 0, 0, 0.05)',
          fill: 'start'
        })

        teamAnswers.push({ alertActive, labels, datasets, average, averageTrend, title, question })
      }
      return teamAnswers
    },

    isUserOwner: state => {
      return get(state.teamOverview, 'isUserOwner')
    }
  }
}
